/**
 * Finding a Lucid document from what somebody pastes.
 *
 * They will paste the address bar, and the address bar is not one shape:
 * an edit link, a view link, an invitation link with a query string, a
 * shared link with a page anchor. All of them carry the same UUID in the
 * same position, so the id is what this looks for and everything else is
 * noise.
 *
 * A bare UUID is accepted too, because somebody who has already read one
 * out of a URL should not have to put it back into one.
 */

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * The document id, or null.
 *
 * Deliberately not a full URL parse: Lucid has moved its path shape more
 * than once — /lucidchart/<id>/edit, /documents/edit/<id>, and the
 * invitation form with the id after a query — and a parser that insists
 * on one of them refuses the others for no reason a person could act on.
 */
export function lucidDocumentId(input) {
  const text = String(input || '').trim();
  if (!text) return null;
  // The FIRST uuid in the string. An invitation link carries a second one
  // — the invitation's own id — after the document's, and taking the last
  // would fetch a document that does not exist.
  const found = UUID.exec(text);
  return found ? found[0].toLowerCase() : null;
}

/** Which page, when the link names one. Lucid writes it as `page=0_0`. */
export function lucidPageId(input) {
  const found = /[?&#]page=([^&#\s]+)/.exec(String(input || ''));
  return found ? decodeURIComponent(found[1]) : null;
}

/**
 * Is this the structured content Lucid's API returns?
 *
 * Checked before anything is drawn, because the failure otherwise is an
 * empty canvas and a shrug: somebody who pastes an SVG export, or a
 * screenshot's JSON, or the wrong half of a response, needs to be told
 * that is what happened rather than left wondering where their chart
 * went.
 */
export function looksLikeLucidPage(value) {
  if (!value || typeof value !== 'object') return false;
  return Array.isArray(value.flowcharts)
    || Array.isArray(value.standaloneClusters)
    || Array.isArray(value.containers?.itemIds);
}

/**
 * The page to draw, out of whatever the API or a paste actually contains.
 *
 * Lucid nests it three deep — pages, then requested chunks, then the data
 * — and a person pasting a response has no idea which layer matters. So
 * every layer is accepted: the whole response, one page, one chunk, or
 * the page object itself.
 *
 * Chunks are MERGED rather than the first one taken. A large chart comes
 * back in nine of them, and importing chunk one is importing a ninth of
 * somebody's chain while appearing to succeed.
 */
export function lucidPageFrom(payload, pageId = null) {
  if (!payload) return null;
  if (looksLikeLucidPage(payload)) return payload;

  const body = typeof payload === 'string' ? safeParse(payload) : payload;
  if (!body) return null;
  if (looksLikeLucidPage(body)) return body;

  const pages = Array.isArray(body.pages) ? body.pages : (body.page ? [body.page] : []);
  if (!pages.length) return null;

  const page = (pageId && pages.find((p) => p.pageId === pageId)) || pages[0];
  const chunks = Array.isArray(page?.requestedChunks) ? page.requestedChunks : [];
  if (!chunks.length) return looksLikeLucidPage(page) ? page : null;

  const merged = { flowcharts: [], standaloneClusters: [] };
  for (const chunk of chunks) {
    const data = chunk?.data || chunk;
    if (Array.isArray(data.flowcharts)) merged.flowcharts.push(...data.flowcharts);
    if (Array.isArray(data.standaloneClusters)) merged.standaloneClusters.push(...data.standaloneClusters);
  }
  return merged;
}

function safeParse(text) {
  try { return JSON.parse(text); } catch { return null; }
}
